import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, BarChart3, CheckCircle2, Database, FileSpreadsheet, FileWarning, Gauge, Globe2, LayoutDashboard, ListFilter, MapPinned, RefreshCw, Settings, ShieldPlus } from 'lucide-react';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';
import { IocManagementTab } from './IocManagementTab';
import { TopVulnerabilitiesPanel } from './TopVulnerabilitiesPanel';
import { SentinelTiObjectBuilder } from './SentinelTiObjectBuilder';
import { ThreatIntelNavigator } from './ThreatIntelNavigator';
import { BulkIocEntryTab } from './BulkIocEntryTab';
import { ThreatLandscapeTab } from './ThreatLandscapeTab';
import { IndicatorLandscapeTab, type IndicatorLandscapePivot } from './IndicatorLandscapeTab';
import { CustomerThreatRelevanceTab } from './CustomerThreatRelevanceTab';

type ThreatIntelSectionId = 'overview' | 'threat_landscape' | 'indicator_landscape' | 'customer_relevance' | 'navigator' | 'ioc_management' | 'manual_ioc' | 'bulk_ioc' | 'top_vulnerabilities' | 'settings';

interface SectionDef {
  id: ThreatIntelSectionId;
  label: string;
  description: string;
  icon: typeof LayoutDashboard;
  capability?: string;
  group: 'Dashboard' | 'Landscape' | 'Indicators' | 'Vulnerabilities' | 'Administration';
}

const SECTIONS: SectionDef[] = [
  { id: 'overview', label: 'Overview', description: 'Threat intelligence summary, source health and quick navigation', icon: LayoutDashboard, capability: 'read', group: 'Dashboard' },
  { id: 'threat_landscape', label: 'Threat Landscape', description: 'Standard/Immersive globe, actor dossiers, customer hunts, MDE exposure, IoC operations and source attribution', icon: Globe2, capability: 'view_threat_landscape', group: 'Landscape' },
  { id: 'indicator_landscape', label: 'Indicator Landscape', description: 'Customer ThreatIntelIndicators inventory, IP geolocation globe, sightings, Hunt Agent pivots and IoC intelligence relationships', icon: MapPinned, capability: 'view_indicator_landscape', group: 'Landscape' },
  { id: 'customer_relevance', label: 'Customer Relevance', description: 'Explainable MSSP actor prioritization using customer targeting context, incidents/alerts, ATT&CK overlap, IoCs and MDE exposure', icon: Gauge, capability: 'view_customer_threat_relevance', group: 'Landscape' },
  { id: 'navigator', label: 'Indicator Navigator', description: 'Sentinel/MDE indicator list, read, delete, revoke and AI analysis', icon: ListFilter, capability: 'list_ioc', group: 'Indicators' },
  { id: 'ioc_management', label: 'IoC Management', description: 'Threat feed reports, suppression controls and Sentinel hunt workflow', icon: Database, capability: 'view_ioc_management', group: 'Indicators' },
  { id: 'manual_ioc', label: 'Manual IoC Entry', description: 'Customer-scoped Sentinel STIX or MDE indicator entry with validation', icon: ShieldPlus, capability: 'create_sentinel_ioc_draft', group: 'Indicators' },
  { id: 'bulk_ioc', label: 'Bulk IoC Entry', description: 'Template-driven customer-scoped bulk validation and upload to Sentinel or MDE', icon: FileSpreadsheet, capability: 'upload_iocs', group: 'Indicators' },
  { id: 'top_vulnerabilities', label: 'KEV / CVE Intelligence', description: 'Ranked CVE board with NVD/CISA/EPSS links', icon: FileWarning, capability: 'view_top_vulnerabilities', group: 'Vulnerabilities' },
  { id: 'settings', label: 'Source Settings', description: 'Feed cache, source readiness and upload integration guidance', icon: Settings, capability: 'manage_ioc_settings', group: 'Administration' },
];

function availableSections(canCapability: (moduleId: string, capability: string) => boolean): SectionDef[] {
  return SECTIONS.filter((section) => !section.capability || canCapability('threat_intel', section.capability));
}

export function IOCUploadTab({ onRunKql }: { onRunKql?: (query: string) => void }) {
  const { activeCustomerId, customers } = useCustomers();
  const { canCapability } = useAuth();
  const activeCustomer = customers.find((c) => c.id === activeCustomerId);
  const [activeSection, setActiveSection] = useState<ThreatIntelSectionId>(() => {
    const saved = localStorage.getItem('ks-ti-active-section') as ThreatIntelSectionId | null;
    return saved && SECTIONS.some((s) => s.id === saved) ? saved : 'overview';
  });

  const visibleSections = useMemo(() => availableSections(canCapability), [canCapability]);

  useEffect(() => {
    if (!visibleSections.some((s) => s.id === activeSection)) {
      setActiveSection(visibleSections[0]?.id ?? 'overview');
    }
  }, [activeSection, visibleSections]);

  useEffect(() => { localStorage.setItem('ks-ti-active-section', activeSection); }, [activeSection]);

  const current = visibleSections.find((s) => s.id === activeSection) ?? visibleSections[0] ?? SECTIONS[0];

  function openNavigatorFromLandscape(indicator: IndicatorLandscapePivot) {
    localStorage.setItem('ks-ti-navigator-focus', JSON.stringify(indicator));
    setActiveSection('navigator');
  }

  function openThreatLandscapeActor(actorId: string) {
    localStorage.setItem('ks-threat-landscape-focus-actor', actorId);
    setActiveSection('threat_landscape');
  }

  function openIndicatorLandscape() {
    setActiveSection('indicator_landscape');
  }

  return (
    <div className="slide-in space-y-5">
      <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 border-b border-border bg-muted/30 px-5 py-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Threat Intelligence</div>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">{current.label}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{current.description}{activeCustomer ? ` · ${activeCustomer.name}` : ''}</p>
          </div>
          <div className="rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold text-muted-foreground">
            {current.group}
          </div>
        </div>
        <ThreatIntelBladeTabs sections={visibleSections} activeSection={current.id} onSelect={setActiveSection} />
      </div>

      <main className="min-w-0 w-full">
        {current.id === 'overview' && <ThreatIntelOverview onSelect={setActiveSection} canCapability={canCapability} />}
        {current.id === 'threat_landscape' && <ThreatLandscapeTab />}
        {current.id === 'indicator_landscape' && <IndicatorLandscapeTab onOpenNavigator={openNavigatorFromLandscape} onOpenThreatLandscapeActor={openThreatLandscapeActor} />}
        {current.id === 'customer_relevance' && <CustomerThreatRelevanceTab onOpenThreatLandscapeActor={openThreatLandscapeActor} onOpenIndicatorLandscape={openIndicatorLandscape} />}
        {current.id === 'navigator' && <ThreatIntelNavigator onRunKql={onRunKql} />}
        {current.id === 'ioc_management' && <IocManagementTab />}
        {current.id === 'top_vulnerabilities' && <TopVulnerabilitiesPanel customerId={activeCustomerId} />}
        {current.id === 'manual_ioc' && <SentinelTiObjectBuilder />}
        {current.id === 'bulk_ioc' && <BulkIocEntryTab />}
        {current.id === 'settings' && <ThreatIntelSettings />}
      </main>
    </div>
  );
}


function ThreatIntelBladeTabs({ sections, activeSection, onSelect }: { sections: SectionDef[]; activeSection: ThreatIntelSectionId; onSelect: (id: ThreatIntelSectionId) => void }) {
  const groups = ['Dashboard', 'Landscape', 'Indicators', 'Vulnerabilities', 'Administration'] as const;
  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap gap-2">
        {groups.map((group) => {
          const groupSections = sections.filter((section) => section.group === group);
          if (!groupSections.length) return null;
          return (
            <div key={group} className="flex flex-wrap items-center gap-1 rounded-xl border border-border bg-background/70 p-1">
              <span className="px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{group}</span>
              {groupSections.map((section) => {
                const Icon = section.icon;
                const active = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => onSelect(section.id)}
                    title={section.description}
                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${active ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                  >
                    <Icon size={14} />
                    {section.label}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ThreatIntelOverview({ onSelect, canCapability }: { onSelect: (id: ThreatIntelSectionId) => void; canCapability: (moduleId: string, capability: string) => boolean }) {
  const cards = SECTIONS.filter((s) => s.id !== 'overview' && (!s.capability || canCapability('threat_intel', s.capability)));
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <InfoCard title="Threat Landscape" value="Actors + Graph" text="Pivot through nation-state actors and source-attributed relationships, then operationalize the selected actor against a customer." />
        <InfoCard title="Indicator Landscape" value="IoCs + Geography" text="Explore the selected customer's latest ThreatIntelIndicators, approximate IP geography, sightings, Hunt Agent pivots and IoC↔actor relationships." />
        <InfoCard title="Customer Relevance" value="Explainable MSSP Risk" text="Rank global threat actors against customer targeting context, incidents/alerts, ATT&CK overlap, IoCs and MDE vulnerability exposure." />
        <InfoCard title="Vulnerability intelligence" value="CISA + NVD + EPSS" text="Prioritize exploitable CVEs with direct links to official CVSS and advisory pages." />
        <InfoCard title="IoC workflows" value="Customer scoped" text="Manual and bulk indicator entry now require an explicit customer and support Sentinel or MDE destinations." />
        <InfoCard title="MDE indicators" value="Manual + bulk" text="Manual and bulk workflows support MDE custom indicators; Navigator adds list/read/revoke/delete and AI analysis." />
      </div>
      <div className="rounded-xl border border-border bg-card p-5 shadow">
        <h3 className="font-semibold text-foreground flex items-center gap-2"><BarChart3 size={18} className="text-primary" /> Threat Intelligence workbench</h3>
        <p className="mt-1 text-sm text-muted-foreground">Use the secondary tabs above to move between Threat Landscape, the customer-scoped Indicator Landscape, Phase 4 Customer Relevance, Sentinel/MDE Navigator, feed hunting, explicit-customer Manual IoC Entry, template-driven Bulk IoC Entry, CVE intelligence and source settings.</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <button key={card.id} type="button" onClick={() => onSelect(card.id)} className="rounded-xl border border-border bg-background p-4 text-left hover:bg-muted transition-colors">
                <Icon size={20} className="text-primary" />
                <div className="mt-3 font-semibold text-foreground">{card.label}</div>
                <div className="mt-1 text-xs text-muted-foreground">{card.description}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function InfoCard({ title, value, text }: { title: string; value: string; text: string }) {
  return <div className="rounded-xl border border-border bg-card p-5 shadow"><div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{title}</div><div className="mt-2 text-xl font-bold text-primary">{value}</div><div className="mt-2 text-xs text-muted-foreground">{text}</div></div>;
}


interface TiSourceStatus {
  keyVault?: { enabled?: boolean; urlConfigured?: boolean; secretNameConfigured?: boolean; identityMode?: "systemAssigned" | "userAssigned"; dedicatedClientIdConfigured?: boolean; legacyAzureClientIdPresent?: boolean; legacyAzureClientIdIgnored?: boolean; status?: string; message?: string };
  nvd?: { configured?: boolean; required?: boolean; secretSource?: string; status?: string; message?: string; keyVaultWarning?: string; reachability?: { status?: string; httpStatus?: number; latencyMs?: number } };
  cisaKev?: { configured?: boolean; status?: string; url?: string };
  epss?: { configured?: boolean; status?: string; url?: string };
  msrc?: { configured?: boolean; status?: string; url?: string };
}

interface TiSourceTestResult {
  name: string;
  status: 'ok' | 'warning' | 'error';
  httpStatus?: number;
  latencyMs: number;
  message: string;
}

interface LandscapeSyncSource {
  sourceKey: string;
  displayName: string;
  status: string;
  mode?: string;
  endpoint?: string;
  lastSyncAt?: string | null;
  lastSuccessAt?: string | null;
  lastError?: string | null;
  objectCount?: number;
  metadata?: Record<string, unknown>;
}

interface LandscapeSyncRun {
  id: string;
  trigger?: string;
  status: string;
  startedAt?: string | null;
  completedAt?: string | null;
  counts?: Record<string, unknown>;
  error?: string | null;
}

interface LandscapeSyncStatus {
  ok: true;
  datasetMode?: string;
  cacheMode?: string;
  cacheDir?: string;
  lastSuccessfulSync?: string | null;
  scheduler?: {
    enabled?: boolean;
    intervalHours?: number;
    inFlight?: boolean;
  };
  summary?: {
    trackedActors?: number;
    campaigns?: number;
    rawStixObjects?: number;
    cisaKevRecords?: number;
    microsoftArticles?: number;
    kloudSecureVulnerabilities?: number;
  };
  sources?: LandscapeSyncSource[];
}

function SourceStatusBadge({ status }: { status?: string }) {
  const normalized = status ?? 'unknown';
  const ok = normalized === 'ok';
  const error = normalized === 'error';
  const classes = ok
    ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-200'
    : error
      ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-200'
      : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-200';

  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${classes}`}>{ok ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />} {normalized}</span>;
}

function ThreatIntelSettings() {
  const { activeCustomerId } = useCustomers();
  const { canCapability } = useAuth();
  const canViewStatus = canCapability('threat_intel', 'view_ti_source_status');
  const canTestSources = canCapability('threat_intel', 'test_ti_sources');
  const canViewLandscapeSyncHistory = canCapability('threat_intel', 'view_threat_sync_history');
  const canSyncLandscape = canCapability('threat_intel', 'sync_threat_landscape');
  const [status, setStatus] = useState<TiSourceStatus | null>(null);
  const [tests, setTests] = useState<TiSourceTestResult[]>([]);
  const [landscapeSync, setLandscapeSync] = useState<LandscapeSyncStatus | null>(null);
  const [landscapeRuns, setLandscapeRuns] = useState<LandscapeSyncRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncingLandscape, setSyncingLandscape] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadStatus() {
    if (!canViewStatus) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<TiSourceStatus>('/threat-intel/source-status');
      setStatus(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load source status.');
    } finally {
      setLoading(false);
    }
  }

  async function loadLandscapeSync() {
    if (!canViewStatus) return;
    try {
      const sync = await apiFetch<LandscapeSyncStatus>('/threat-intel/landscape/sync/status');
      setLandscapeSync(sync);
      if (canViewLandscapeSyncHistory) {
        const history = await apiFetch<{ ok: true; runs?: LandscapeSyncRun[] }>('/threat-intel/landscape/sync/history?limit=8');
        setLandscapeRuns(history.runs ?? []);
      } else {
        setLandscapeRuns([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load Threat Landscape sync status.');
    }
  }

  async function syncThreatLandscapeNow() {
    if (!canSyncLandscape) return;
    setSyncingLandscape(true);
    setError(null);
    try {
      await apiFetch('/threat-intel/landscape/sync', { method: 'POST' });
      await loadLandscapeSync();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Threat Landscape synchronization failed.');
    } finally {
      setSyncingLandscape(false);
    }
  }

  async function testSources() {
    if (!canTestSources) return;
    setTesting(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (activeCustomerId) qs.set('customerId', activeCustomerId);
      const data = await apiFetch<{ results: TiSourceTestResult[] }>(`/threat-intel/test-sources?${qs.toString()}`, { method: 'POST' });
      setTests(data.results ?? []);
      await Promise.all([loadStatus(), loadLandscapeSync()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Source test failed.');
    } finally {
      setTesting(false);
    }
  }

  useEffect(() => {
    void loadStatus();
    void loadLandscapeSync();
  }, [canViewStatus, canViewLandscapeSyncHistory]);

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-border bg-card p-5 shadow space-y-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <h3 className="font-semibold text-foreground">Threat Intelligence Settings</h3>
            <p className="mt-1 text-sm text-muted-foreground">Source configuration for vulnerability intelligence plus the server-side Threat Landscape synchronization pipeline: MITRE ATT&CK TAXII/STIX, CISA KEV, Microsoft threat research and KloudSecure enrichment.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => void loadStatus()} disabled={!canViewStatus || loading} className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs font-semibold hover:bg-muted disabled:opacity-50"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh status</button>
            <button type="button" onClick={() => void testSources()} disabled={!canTestSources || testing} className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"><RefreshCw size={14} className={testing ? 'animate-spin' : ''} /> Test all sources</button>
          </div>
        </div>
        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-200">{error}</div>}
        {!canViewStatus && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">You do not have permission to view TI source status.</div>}
        {status && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <SourceCard title="NVD CVE API" status={status.nvd?.status} body={status.nvd?.configured ? `API key active from ${status.nvd.secretSource === 'keyVault' ? 'Key Vault' : status.nvd.secretSource === 'env' ? 'environment variable' : status.nvd.secretSource}.` : 'NVD public API reachable; no API key active, public rate-limit mode is active.'} foot={status.nvd?.message} />
            <SourceCard title="CISA KEV" status={status.cisaKev?.status} body="Public known exploited vulnerabilities catalog." foot={status.cisaKev?.url} />
            <SourceCard title="FIRST EPSS" status={status.epss?.status} body="Public exploit probability enrichment API." foot={status.epss?.url} />
            <SourceCard title="MSRC CVRF" status={status.msrc?.status} body="Public Microsoft Security Update Guide/CVRF feed." foot={status.msrc?.url} />
          </div>
        )}
        {status && (
          <div className="rounded-xl border border-border bg-background p-4 text-xs text-muted-foreground">
            <div className="font-semibold text-foreground mb-2">Key Vault configuration</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-2">
              <div>Key Vault enabled: <strong className="text-foreground">{status.keyVault?.enabled ? 'Yes' : 'No'}</strong></div>
              <div>Vault URL configured: <strong className="text-foreground">{status.keyVault?.urlConfigured ? 'Yes' : 'No'}</strong></div>
              <div>NVD secret name configured: <strong className="text-foreground">{status.keyVault?.secretNameConfigured ? 'Yes' : 'No'}</strong></div>
              <div>Identity mode: <strong className="text-foreground">{status.keyVault?.identityMode === 'userAssigned' ? 'User-assigned MI' : 'System-assigned VM MI'}</strong></div>
              <div>User-assigned client ID: <strong className="text-foreground">{status.keyVault?.dedicatedClientIdConfigured ? 'Configured' : 'Not configured'}</strong></div>
            </div>
            {status.keyVault?.legacyAzureClientIdPresent && (
              <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-2 text-[11px] text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
                Diagnostic: AZURE_CLIENT_ID is present in the runtime environment but is ignored for Key Vault managed identity selection. Use KEY_VAULT_MANAGED_IDENTITY_CLIENT_ID only for user-assigned MI.
              </div>
            )}
            {status.keyVault?.message && <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">Key Vault warning: {status.keyVault.message}</div>}
          </div>
        )}
      </div>


      {landscapeSync && (
        <div className="rounded-xl border border-cyan-500/20 bg-card p-5 shadow space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-cyan-500">Threat Landscape Intelligence Sync</div>
              <h4 className="mt-1 font-semibold text-foreground">Server-side CTI normalization and cache</h4>
              <p className="mt-1 max-w-4xl text-xs leading-5 text-muted-foreground">
                MITRE ATT&amp;CK TAXII 2.1 is the canonical actor/relationship source. CISA KEV, Microsoft threat research and the existing KloudSecure vulnerability cache enrich the normalized PostgreSQL dataset. Raw source snapshots remain on the application VM; the browser only receives lightweight landscape records.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => void loadLandscapeSync()} disabled={!canViewStatus || syncingLandscape} className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs font-semibold hover:bg-muted disabled:opacity-50">
                <RefreshCw size={14} /> Refresh
              </button>
              <button type="button" onClick={() => void syncThreatLandscapeNow()} disabled={!canSyncLandscape || syncingLandscape} className="inline-flex items-center gap-2 rounded-lg bg-cyan-500 px-3 py-2 text-xs font-bold text-slate-950 hover:bg-cyan-400 disabled:opacity-50">
                <RefreshCw size={14} className={syncingLandscape ? 'animate-spin' : ''} /> {syncingLandscape ? 'Synchronizing…' : 'Sync now'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <InfoCard title="Actors" value={String(landscapeSync.summary?.trackedActors ?? 0)} text="Normalized ATT&CK groups" />
            <InfoCard title="Raw STIX" value={String(landscapeSync.summary?.rawStixObjects ?? 0)} text="Server-side ATT&CK objects" />
            <InfoCard title="Campaigns" value={String(landscapeSync.summary?.campaigns ?? 0)} text="ATT&CK campaign objects" />
            <InfoCard title="CISA KEV" value={String(landscapeSync.summary?.cisaKevRecords ?? 0)} text="Known exploited CVEs" />
            <InfoCard title="Microsoft" value={String(landscapeSync.summary?.microsoftArticles ?? 0)} text="Matched threat articles" />
            <InfoCard title="KloudSecure CVEs" value={String(landscapeSync.summary?.kloudSecureVulnerabilities ?? 0)} text="Existing CVE enrichment cache" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {(landscapeSync.sources ?? []).map((source) => (
              <div key={source.sourceKey} className="rounded-xl border border-border bg-background p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-foreground">{source.displayName}</div>
                    <div className="mt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">{source.mode || source.sourceKey}</div>
                  </div>
                  <SourceStatusBadge status={source.status} />
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                  Objects: <strong className="text-foreground">{source.objectCount ?? 0}</strong>
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">Last success: {source.lastSuccessAt ? new Date(source.lastSuccessAt).toLocaleString() : 'Not yet synchronized'}</div>
                {source.lastError && <div className="mt-2 text-[11px] leading-4 text-amber-600 dark:text-amber-300">{source.lastError}</div>}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 text-xs">
            <div className="rounded-xl border border-border bg-background p-4">
              <div className="font-semibold text-foreground">Dataset mode</div>
              <div className="mt-2 text-muted-foreground">{landscapeSync.datasetMode || 'seed-fallback'}</div>
              <div className="mt-1 text-[11px] text-muted-foreground">Last successful sync: {landscapeSync.lastSuccessfulSync ? new Date(landscapeSync.lastSuccessfulSync).toLocaleString() : 'Not yet synchronized'}</div>
            </div>
            <div className="rounded-xl border border-border bg-background p-4">
              <div className="font-semibold text-foreground">Scheduler</div>
              <div className="mt-2 text-muted-foreground">{landscapeSync.scheduler?.enabled ? `Enabled · every ${landscapeSync.scheduler.intervalHours ?? 12}h` : 'Disabled'}</div>
              <div className="mt-1 text-[11px] text-muted-foreground">{landscapeSync.scheduler?.inFlight ? 'A synchronization is currently in flight.' : 'No synchronization currently in flight.'}</div>
            </div>
            <div className="rounded-xl border border-border bg-background p-4">
              <div className="font-semibold text-foreground">Raw snapshot cache</div>
              <div className="mt-2 break-all text-muted-foreground">{landscapeSync.cacheDir || 'Server-side cache directory'}</div>
              <div className="mt-1 text-[11px] text-muted-foreground">Mode: {landscapeSync.cacheMode || 'VM cache + PostgreSQL'}</div>
            </div>
          </div>

          {!canSyncLandscape && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">Your role can view synchronization health but cannot start an on-demand Threat Landscape sync.</div>}

          {canViewLandscapeSyncHistory && landscapeRuns.length > 0 && (
            <div>
              <div className="font-semibold text-foreground">Recent synchronization runs</div>
              <div className="mt-2 overflow-x-auto rounded-xl border border-border">
                <table className="min-w-full text-xs">
                  <thead className="bg-muted/40 text-muted-foreground"><tr><th className="px-3 py-2 text-left">Started</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Actors</th><th className="px-3 py-2 text-left">Raw STIX</th><th className="px-3 py-2 text-left">Message</th></tr></thead>
                  <tbody className="divide-y divide-border">
                    {landscapeRuns.map((run) => <tr key={run.id}><td className="px-3 py-2 text-muted-foreground">{run.startedAt ? new Date(run.startedAt).toLocaleString() : '—'}</td><td className="px-3 py-2"><SourceStatusBadge status={run.status === 'succeeded' ? 'ok' : run.status === 'failed' ? 'error' : 'warning'} /></td><td className="px-3 py-2 text-foreground">{Number(run.counts?.['actors'] ?? 0)}</td><td className="px-3 py-2 text-foreground">{Number(run.counts?.['rawStixObjects'] ?? 0)}</td><td className="px-3 py-2 max-w-xl text-muted-foreground">{run.error || run.trigger || '—'}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {tests.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5 shadow">
          <h4 className="font-semibold text-foreground">Latest source test results</h4>
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground"><tr><th className="text-left py-2 pr-3">Source</th><th className="text-left py-2 pr-3">Status</th><th className="text-left py-2 pr-3">HTTP</th><th className="text-left py-2 pr-3">Latency</th><th className="text-left py-2">Message</th></tr></thead>
              <tbody className="divide-y divide-border">
                {tests.map((t) => <tr key={t.name}><td className="py-2 pr-3 font-medium text-foreground">{t.name}</td><td className="py-2 pr-3"><SourceStatusBadge status={t.status} /></td><td className="py-2 pr-3 text-muted-foreground">{t.httpStatus ?? '—'}</td><td className="py-2 pr-3 text-muted-foreground">{t.latencyMs} ms</td><td className="py-2 text-muted-foreground">{t.message}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-4 shadow"><div className="font-semibold text-foreground">Optional environment settings</div><ul className="mt-2 list-disc pl-4 text-xs text-muted-foreground space-y-1"><li>TI_USE_KEY_VAULT=true</li><li>KEY_VAULT_URL=https://&lt;vault&gt;.vault.azure.net/</li><li>TI_NVD_API_KEY_SECRET_NAME=kloudsecure-ti-nvd-api-key</li><li>KEY_VAULT_MANAGED_IDENTITY_CLIENT_ID only when using user-assigned MI.</li><li>NVD_API_KEY fallback remains optional.</li><li>THREAT_LANDSCAPE_SYNC_ENABLED=true</li><li>THREAT_LANDSCAPE_SYNC_INTERVAL_HOURS=12</li><li>THREAT_LANDSCAPE_CACHE_DIR=/var/lib/kloudsecure/threat-landscape/cache</li></ul></div>
        <div className="rounded-xl border border-border bg-card p-4 shadow"><div className="font-semibold text-foreground">Upload integrations</div><ul className="mt-2 list-disc pl-4 text-xs text-muted-foreground space-y-1"><li>Sentinel upload requires Microsoft Sentinel Contributor on the workspace.</li><li>MDE upload requires Ti.ReadWrite.All application permission and admin consent.</li><li>Both upload actions are captured by the existing KloudSecure audit middleware.</li></ul></div>
      </div>
    </div>
  );
}

function SourceCard({ title, status, body, foot }: { title: string; status?: string; body: string; foot?: string }) {
  return <div className="rounded-xl border border-border bg-background p-4"><div className="flex items-center justify-between gap-2"><div className="font-semibold text-foreground">{title}</div><SourceStatusBadge status={status} /></div><p className="mt-2 text-xs text-muted-foreground">{body}</p>{foot && <p className="mt-2 break-all text-[11px] text-muted-foreground">{foot}</p>}</div>;
}

